import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { toast, Slide as ToastSlide } from "react-toastify";
import cx from "classnames";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import Collapse from "@material-ui/core/Collapse";

// @material-ui/icons
import { FilterNone, ExpandMore, ExpandLess, Info } from "@material-ui/icons";

import {
  grayColor,
  whiteColor,
  dangerColor,
  warningColor,
} from "../../assets/jss/material-dashboard-pro-react";

import useThemeColors from "../../hooks/useThemeColors";

// Inputs que não devem ser sugeridos para revisão (são referências base)
const NON_REVIEWABLE_INPUTS = [
  "ventas",
  "sales",
  "vendas",
  "faturamento",
  "facturacion",
];

// Número máximo de exemplos a exibir
const MAX_EXAMPLES_DISPLAY = 10;

// Mock data kept for reference - component now uses real data from API
// eslint-disable-next-line no-unused-vars
const _MOCK_LOG_DATA = {
  meta: {
    implementation_id: 10007,
    implementation_app: "ccu_chile",
    language: "es",
    year: 2024,
    month: 1,
    started_at: "2026-01-20T11:49:16.002597Z",
    finished_at: "2026-01-20T11:55:58.082464Z",
    duration_seconds: 402,
  },
  summary: {
    revenue_total: 227574537763.85,
    revenue_loss_value: 8993881010.85,
    revenue_loss_pct: 4.0,
    cost_total: 218501120313.0,
    cost_loss_value: 481772602.04,
    cost_loss_pct: 0.2,
  },
  chapters: [
    {
      id: "cruzamiento_con_ventas",
      name: "Cruzamiento con Ventas",
      inputs: [
        "ventas",
        "maestro_productos",
        "maestro_clientes",
        "maestro_cadenas",
      ],
      revenue_loss_contribution: 1.0,
      cost_loss_contribution: 0,
      conclusion: {
        summary: "Base final: 3.531.601 filas | $219B ingreso bruto",
        coverage_pct: 96.0,
        total_loss_value: 8993881010.85,
        losses: [
          {
            message: "SKUs de ventas no encontrados en maestro_productos",
            count: 9134,
            value: 85671891.0,
            pct: 0.3,
            source: "Cruzamiento ventas x maestros",
            related_inputs: ["maestro_productos"],
          },
          {
            message: "clientes de ventas no encontrados en maestro_clientes",
            count: 33291,
            value: 8908209119.85,
            pct: 3.9,
            source: "Cruzamiento ventas x maestros",
            related_inputs: ["maestro_clientes"],
          },
        ],
        quality_issues: [
          {
            message: "duplicados en maestro_productos",
            issue_type: "duplicates",
            removed: 45,
            source: "Carga y limpieza de maestros",
            related_inputs: ["maestro_productos"],
          },
          {
            message: "duplicados en maestro_clientes",
            issue_type: "duplicates",
            removed: 128,
            source: "Carga y limpieza de maestros",
            related_inputs: ["maestro_clientes"],
          },
        ],
      },
      sections: [
        {
          id: "carga_y_limpieza_de_maestros",
          name: "Carga y limpieza de maestros",
          entries: [
            {
              type: "quality_issue",
              message: "duplicados en maestro_productos",
              data: {
                issue_type: "duplicates",
                removed_count: 45,
                total_count: 12500,
                removed_pct: 0.4,
              },
              elements:
                "450123,450124,870555,870556,451001,451002,451003,451004,451005,451006,451007,451008,451009,451010,451011,451012,451013,451014,451015,451016,451017,451018,451019,451020,451021,451022,451023,451024,451025,451026,451027,451028,451029,451030,451031,451032,451033,451034,451035,451036,451037,451038,451039,451040,451041",
              related_inputs: ["maestro_productos"],
            },
            {
              type: "quality_issue",
              message: "duplicados en maestro_clientes",
              data: {
                issue_type: "duplicates",
                removed_count: 128,
                total_count: 85000,
                removed_pct: 0.2,
              },
              elements: "1234567,2345678",
              related_inputs: ["maestro_clientes"],
            },
          ],
          conclusion: {
            summary: "Maestros cargados: 8.475 productos | 1.236.015 clientes",
            quality_issues: [
              { message: "duplicados en maestro_productos", removed: 45 },
              { message: "duplicados en maestro_clientes", removed: 128 },
            ],
          },
        },
        {
          id: "cruzamiento_ventas_x_maestros",
          name: "Cruzamiento ventas x maestros",
          entries: [
            {
              type: "loss",
              message: "SKUs de ventas no encontrados en maestro_productos",
              data: {
                total_count: 3574026,
                lost_count: 9134,
                lost_pct: 0.3,
                lost_value: 85671891.0,
                total_value: 227574537763.85,
                is_revenue: true,
              },
              elements:
                "999001,999002,999003,999004,999005,999006,999007,999008,999009,999010",
              related_inputs: ["maestro_productos"],
            },
            {
              type: "loss",
              message: "clientes de ventas no encontrados en maestro_clientes",
              data: {
                total_count: 3564892,
                lost_count: 33291,
                lost_pct: 3.9,
                lost_value: 8908209119.85,
                total_value: 227488865872.85,
                is_revenue: true,
              },
              elements: "5555555,6666666,7777777,8888888,9999999",
              related_inputs: ["maestro_clientes"],
            },
          ],
          conclusion: {
            summary: "96.0% de ventas cruzadas - $8.99B sin procesar",
            coverage_pct: 96.0,
            total_loss_value: 8993881010.85,
            losses: [
              {
                message: "SKUs no encontrados",
                count: 9134,
                value: 85671891.0,
              },
              {
                message: "clientes no encontrados",
                count: 33291,
                value: 8908209119.85,
              },
            ],
          },
        },
      ],
    },
    {
      id: "rappel",
      name: "Rappel",
      inputs: [
        "rappel_cervezas_eccusa",
        "media_movil_rappel_manual",
        "rappel_maestro_marca_cervezas",
        "rappel_maestro_marca_eccusa",
        "rappel_maestro_cadenas",
        "informe_rappel_vspt",
        "gastos_diversos_piscos",
      ],
      revenue_loss_contribution: 0,
      cost_loss_contribution: 0.02,
      conclusion: {
        summary:
          "$6.01B asignado (automatico: $5.2B | vinos: $389M | piscos: $280M)",
        coverage_pct: 97.9,
        total_loss_value: 9178311.07,
        breakdown: {
          automatico: 5200000000,
          vinos: 389000000,
          piscos: 280000000,
        },
        losses: [
          {
            message: "ruts de vinos no encontrados en maestro de cadenas",
            count: 30,
            value: 9178311.07,
            pct: 2.1,
            source: "Rappel Vinos",
            related_inputs: ["informe_rappel_vspt", "rappel_maestro_cadenas"],
            elements:
              "6264537,76012578,76021762,76050255,76128330,76173888,76340497,76340813,76353910,76441100,76454970,76986494,76995782,77027930,77038620,77194160,77327430,77335750,77498518,77575340,77587770,77772030,78129870,78494010,78555310,78785190,81096600,84820900,87765000,9187715",
          },
        ],
      },
      sections: [
        {
          id: "rappel_automatico",
          name: "Rappel Automatico - Cervezas y ECCUSA",
          entries: [
            {
              type: "cascade",
              message: "rappel automatico asignado por marca/cadena",
              data: {
                total_value: 5200000000,
                steps: [
                  { key: "Cervezas", value: 4100000000, pct: 78.8 },
                  { key: "ECCUSA", value: 1100000000, pct: 21.2 },
                ],
              },
            },
          ],
          conclusion: {
            summary: "$5.2B asignado a 45.000 combinaciones cliente/SKU",
          },
        },
        {
          id: "rappel_vinos",
          name: "Rappel Vinos",
          entries: [
            {
              type: "loss",
              message: "ruts de vinos no encontrados en maestro de cadenas",
              data: {
                lost_count: 30,
                lost_pct: 2.1,
                lost_value: 9178311.07,
                total_value: 440985790.75,
                is_cost: true,
              },
              elements:
                "6264537,76012578,76021762,76050255,76128330,76173888,76340497,76340813,76353910,76441100,76454970,76986494,76995782,77027930,77038620,77194160,77327430,77335750,77498518,77575340,77587770,77772030,78129870,78494010,78555310,78785190,81096600,84820900,87765000,9187715",
              related_inputs: ["informe_rappel_vspt", "rappel_maestro_cadenas"],
            },
          ],
          conclusion: {
            summary: "$431.8M asignado - 30 RUTs sin cadena ($9.2M)",
            coverage_pct: 97.9,
            total_loss_value: 9178311.07,
            losses: [
              { message: "ruts no encontrados", count: 30, value: 9178311.07 },
            ],
          },
        },
        {
          id: "rappel_piscos",
          name: "Rappel Piscos",
          entries: [
            {
              type: "info",
              message: "rappel piscos procesado via gastos diversos",
              data: { value: 280, suffix: "M" },
            },
          ],
          conclusion: {
            summary: "$280M asignado proporcionalmente",
          },
        },
      ],
    },
    {
      id: "costos_directos",
      name: "Costos Directos",
      inputs: ["costos_directos_auto", "maestro_productos"],
      revenue_loss_contribution: 0,
      cost_loss_contribution: 0.03,
      conclusion: {
        summary: "$89.5B en costos directos asignados",
        coverage_pct: 99.99,
        total_loss_value: 12178236.0,
        losses: [
          {
            message: "SKUs sin definicion de costos directos",
            count: 4,
            value: 12178236.0,
            source: "Imputacion Costos Kuntsmann",
            elements: "447030,447033,846255,4989",
            related_inputs: ["costos_directos_auto"],
          },
        ],
      },
      sections: [
        {
          id: "imputacion_costos_kuntsmann",
          name: "Imputacion Costos Kuntsmann",
          entries: [
            {
              type: "cascade",
              message: "costos directos Kuntsmann imputados",
              data: {
                total_items: 7,
                total_processed: 7,
                total_remaining: 0,
                steps: [
                  { key: "descr_pinta + formato", count: 0 },
                  { key: "marca + formato", count: 1 },
                  { key: "marca + descr_capac_envase", count: 3 },
                  { key: "marca", count: 3 },
                ],
              },
            },
            {
              type: "loss",
              message: "SKUs sin definicion de costos directos",
              data: {
                lost_count: 4,
                lost_pct: 0.0,
                lost_value: 12178236.0,
                total_value: 218501120313.0,
                is_cost: true,
              },
              elements: "447030,447033,846255,4989",
              related_inputs: ["costos_directos_auto"],
            },
          ],
          conclusion: {
            summary: "7/7 SKUs Kuntsmann imputados | 4 SKUs sin costo ($12.2M)",
            coverage_pct: 99.99,
            total_loss_value: 12178236.0,
          },
        },
      ],
    },
    {
      id: "gastos_fabricacion",
      name: "Gastos Fabricacion",
      inputs: [
        "gastos_fabricacion_cervezas",
        "gastos_fabricacion_analcoholic",
        "gastos_fabricacion_vinos",
        "media_movil_gastos_fabricacion",
      ],
      revenue_loss_contribution: 0,
      cost_loss_contribution: 0,
      conclusion: {
        summary: "$28.7B en gastos fabricacion asignados",
        coverage_pct: 100.0,
        breakdown: {
          cervezas: 18200000000,
          analcoholic: 5100000000,
          vinos: 5400000000,
        },
      },
      sections: [
        {
          id: "imputacion_cervezas",
          name: "Imputacion Cervezas",
          entries: [
            {
              type: "cascade",
              message: "imputacion de gastos fabricacion cervezas",
              data: {
                total_items: 45,
                total_processed: 45,
                total_remaining: 0,
                steps: [
                  { key: "descr_pinta + formato", count: 2 },
                  { key: "agrup_marca + formato", count: 6 },
                  { key: "agrup_marca + descr_capac_envase", count: 10 },
                  { key: "formato", count: 14 },
                  { key: "descr_capac_envase", count: 9 },
                  { key: "descr_envase", count: 1 },
                  { key: "media general", count: 3 },
                ],
              },
            },
          ],
          conclusion: {
            summary: "45/45 SKUs cervezas imputados",
          },
        },
        {
          id: "imputacion_analcoholic",
          name: "Imputacion Analcoholic",
          entries: [
            {
              type: "cascade",
              message: "imputacion de gastos fabricacion analcoholic",
              data: {
                total_items: 81,
                total_processed: 81,
                total_remaining: 0,
                steps: [
                  { key: "descr_pinta + formato", count: 5 },
                  { key: "agrup_marca + formato", count: 6 },
                  { key: "agrup_marca + descr_capac_envase", count: 27 },
                  { key: "descr_tipo_prod + descr_capac_envase", count: 27 },
                  { key: "formato", count: 1 },
                  { key: "descr_envase", count: 3 },
                  { key: "media general", count: 12 },
                ],
              },
            },
          ],
          conclusion: {
            summary: "81/81 SKUs analcoholic imputados",
          },
        },
      ],
    },
    {
      id: "acarreo",
      name: "Acarreo",
      inputs: [
        "costo_pallet_acarreo_agreg",
        "porteo_transportes",
        "maestro_cds_rutas_transp",
        "pnl_transportes",
      ],
      revenue_loss_contribution: 0,
      cost_loss_contribution: 0.06,
      conclusion: {
        summary: "$6.3B en acarreo y CDs asignado",
        coverage_pct: 95.4,
        total_loss_value: 287533452.45,
        losses: [
          {
            message: "CDs no asignados",
            value: 287533452.45,
            pct: 4.6,
            source: "Asignacion de costos CD",
          },
        ],
      },
      sections: [
        {
          id: "asignacion_de_costos_cd",
          name: "Asignacion de costos CD",
          entries: [
            {
              type: "cascade",
              message: "asignacion de costos de acarreo y CDs",
              data: {
                total_value: 6262414708.0,
                steps: [
                  {
                    key: "CDs directo asignado",
                    value: 5974881255.55,
                    pct: 95.4,
                  },
                  { key: "Acarreo asignado", value: 5568209856.95, pct: 99.2 },
                ],
                not_allocated_value: 287533452.45,
                not_allocated_pct: 4.6,
              },
            },
          ],
          conclusion: {
            summary: "$5.97B asignado directo - $287M sin asignar (4.6%)",
            coverage_pct: 95.4,
          },
        },
      ],
    },
    {
      id: "merchandising",
      name: "Merchandising",
      inputs: [
        "costos_ejecutores_camionetas",
        "reponedores",
        "de_para_subgerencias_repos",
      ],
      revenue_loss_contribution: 0,
      cost_loss_contribution: 0.06,
      conclusion: {
        summary: "$2.0B en merchandising asignado",
        coverage_pct: 87.8,
        total_loss_value: 283966954.98,
        breakdown: {
          ejecutores_camionetas: 284080875.04,
          reponedores: 1750018712.04,
        },
        losses: [
          {
            message: "clientes ejecutores/camionetas no encontrados",
            count: 174,
            value: 6523146.09,
            source: "Ejecutores y Camionetas",
            related_inputs: ["costos_ejecutores_camionetas"],
          },
          {
            message: "salas de supermercados no encontradas",
            count: 140,
            value: 277443808.89,
            source: "Reponedores Supermercados",
            related_inputs: ["reponedores"],
          },
        ],
      },
      sections: [
        {
          id: "ejecutores_y_camionetas",
          name: "Ejecutores y Camionetas",
          entries: [
            {
              type: "loss",
              message:
                "clientes de ejecutores/camionetas no encontrados en ventas",
              data: {
                total_count: 8820,
                lost_count: 174,
                lost_pct: 2.0,
                lost_value: 6523146.09,
                total_value: 290604022.13,
                is_cost: true,
              },
              elements:
                "660364,1303556,528452,665316,593158,2009820,2331735,2193394,1658617,783667",
              related_inputs: ["costos_ejecutores_camionetas"],
            },
          ],
          conclusion: {
            summary: "$284M asignado - 174 clientes sin match ($6.5M)",
            coverage_pct: 97.8,
            total_loss_value: 6523146.09,
          },
        },
        {
          id: "reponedores_supermercados",
          name: "Reponedores Supermercados",
          entries: [
            {
              type: "loss",
              message: "salas de supermercados no encontradas en ventas",
              data: {
                total_count: 995,
                lost_count: 140,
                lost_pct: 14.1,
                lost_value: 277443808.89,
                total_value: 2027425520.93,
                is_cost: true,
              },
              elements:
                "680549,680539,678884,2390832,680196,678883,680609,680587,680567,2468293",
              related_inputs: ["reponedores"],
            },
          ],
          conclusion: {
            summary: "$1.75B asignado - 140 salas sin match ($277M)",
            coverage_pct: 86.3,
            total_loss_value: 277443808.89,
          },
        },
      ],
    },
    {
      id: "depreciacion_de_equipos",
      name: "Depreciacion de Equipos",
      inputs: [
        "base_activos",
        "gastos_contables_cervezas",
        "gastos_contables_eccusa",
      ],
      revenue_loss_contribution: 0,
      cost_loss_contribution: 0.1,
      conclusion: {
        summary: "$557M en depreciacion de equipos asignada",
        coverage_pct: 92.0,
        total_loss_value: 48332648.0,
        losses: [
          {
            message: "clientes con activos no encontrados en ventas",
            count: 4755,
            value: 46481359.0,
            source: "Asignacion de activos",
            related_inputs: ["base_activos"],
          },
          {
            message: "clientes con activos pero sin cervezas/analcoholicos",
            count: 77,
            value: 1007900.0,
            source: "Asignacion de activos",
            related_inputs: ["base_activos"],
          },
          {
            message: "clientes con activos pero ingreso bruto cero",
            count: 35,
            value: 843389.0,
            source: "Asignacion de activos",
            related_inputs: ["base_activos"],
          },
        ],
      },
      sections: [
        {
          id: "asignacion_de_activos",
          name: "Asignacion de activos",
          entries: [
            {
              type: "loss",
              message: "clientes con activos no encontrados en ventas",
              data: {
                lost_count: 4755,
                lost_pct: 7.7,
                lost_value: 46481359.0,
                total_value: 605135154.52,
                is_cost: true,
              },
              elements:
                "2230018,2245714,2282933,680539,1365275,1379410,1811266,680473,2042031,573889",
              related_inputs: ["base_activos"],
            },
            {
              type: "loss",
              message: "clientes con activos pero sin cervezas/analcoholicos",
              data: {
                lost_count: 77,
                lost_pct: 0.2,
                lost_value: 1007900.0,
                total_value: 605135154.52,
                is_cost: true,
              },
              elements:
                "2237246,643725,1752543,2228813,2224764,1652447,1422191,2080194,2079793,1954573",
              related_inputs: ["base_activos"],
            },
            {
              type: "loss",
              message: "clientes con activos pero ingreso bruto cero",
              data: {
                lost_count: 35,
                lost_pct: 0.1,
                lost_value: 843389.0,
                total_value: 605135154.52,
                is_cost: true,
              },
              elements:
                "1952589,680894,706819,648818,705310,1987973,2298074,790112,1658500,816490",
              related_inputs: ["base_activos"],
            },
          ],
          conclusion: {
            summary: "$557M asignado - $48.3M no asignado (8.0%)",
            coverage_pct: 92.0,
            total_loss_value: 48332648.0,
          },
        },
      ],
    },
    {
      id: "marketing",
      name: "Marketing",
      inputs: [
        "marketing_cervezas",
        "marketing_vinos",
        "de_para_marca",
        "de_para_btl",
        "informe_ventas_marketing",
      ],
      revenue_loss_contribution: 0,
      cost_loss_contribution: 0.08,
      conclusion: {
        summary: "$12.8B en marketing asignado",
        coverage_pct: 95.8,
        total_loss_value: 36705784.0,
        losses: [
          {
            message: "combinaciones BTL no encontradas",
            count: 1,
            value: 36705784.0,
            source: "BTL",
            related_inputs: ["marketing_cervezas", "de_para_btl"],
          },
        ],
      },
      sections: [
        {
          id: "btl",
          name: "BTL",
          entries: [
            {
              type: "loss",
              message: "combinaciones BTL no encontradas en la base de ventas",
              data: {
                lost_count: 1,
                lost_pct: 4.2,
                lost_value: 36705784.0,
                total_value: 867411984.0,
                is_cost: true,
              },
              table_columns: ["marca", "canal", "segmento", "imagen", "gasto"],
              table_rows: [["mahina", "onpremise", "0", "i", 36705784.0]],
              related_inputs: [
                "marketing_cervezas",
                "de_para_btl",
                "de_para_marca",
              ],
            },
          ],
          conclusion: {
            summary: "$830M BTL asignado - 1 combinacion sin match ($36.7M)",
            coverage_pct: 95.8,
            total_loss_value: 36705784.0,
          },
        },
        {
          id: "atl",
          name: "ATL",
          entries: [
            {
              type: "info",
              message: "ATL asignado proporcionalmente por marca",
              data: { value: 11.9, suffix: "B" },
            },
          ],
          conclusion: {
            summary: "$11.9B ATL asignado",
          },
        },
      ],
    },
  ],
};

const useStyles = makeStyles(() => ({
  root: {
    width: "100%",
    fontSize: "14px",
  },
  // Monospace for values/codes only
  monoValue: {
    fontFamily: "'Roboto Mono', 'Consolas', monospace",
  },
  // Chapter Card
  chapterCard: {
    marginBottom: "16px",
    border: `1px solid ${grayColor[8]}`,
    borderRadius: "6px",
    overflow: "hidden",
    backgroundColor: whiteColor,
  },
  chapterHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    backgroundColor: grayColor[13],
    cursor: "pointer",
    userSelect: "none",
  },
  chapterTitle: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontWeight: 600,
    fontSize: "14px",
    flex: 1,
    minWidth: 0,
  },
  chapterHeaderSummary: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "12px",
    color: grayColor[3],
    marginLeft: "16px",
    flex: "0 1 auto",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  headerSummaryText: {
    color: grayColor[2],
  },
  headerSummarySeparator: {
    color: grayColor[4],
  },
  headerCoverage: {
    fontWeight: 500,
  },
  chapterNumber: {
    backgroundColor: (props) => props.clientColor || grayColor[2],
    color: (props) => props.clientTextColor || whiteColor,
    width: "24px",
    height: "24px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: 700,
  },
  chapterName: {
    color: (props) => props.clientColor || grayColor[1],
  },
  chapterMeta: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  lossBadge: {
    padding: "2px 8px",
    borderRadius: "3px",
    fontSize: "11px",
    fontWeight: 500,
  },
  lossBadgeRevenue: {
    backgroundColor: `${dangerColor[1]}15`,
    color: dangerColor[1],
    marginLeft: "12px",
  },
  lossBadgeCost: {
    backgroundColor: `${warningColor[1]}15`,
    color: warningColor[1],
    marginLeft: "12px",
  },
  lossBadgeNone: {
    backgroundColor: grayColor[5],
    color: grayColor[1],
    marginLeft: "12px",
  },
  expandIcon: {
    color: grayColor[3],
  },
  // Inputs line
  chapterInputs: {
    padding: "8px 16px",
    backgroundColor: whiteColor,
    borderBottom: `1px solid ${grayColor[5]}`,
    fontSize: "12px",
    color: grayColor[3],
  },
  inputsLabel: {
    color: grayColor[2],
    marginRight: "6px",
  },
  // Summary/Conclusion area (no title)
  chapterSummary: {
    padding: "16px",
    backgroundColor: whiteColor,
    borderBottom: `1px solid ${grayColor[5]}`,
  },
  summaryLine: {
    fontSize: "12px",
    color: grayColor[1],
    marginBottom: "4px",
    fontWeight: 500,
  },
  coverageLine: {
    fontSize: "12px",
    color: grayColor[2],
    marginBottom: "12px",
  },
  coverageValue: {
    fontWeight: 600,
    color: grayColor[1],
  },
  // Losses breakdown in summary
  lossesBreakdown: {
    marginTop: "12px",
    paddingTop: "12px",
    borderTop: `1px dashed ${grayColor[5]}`,
  },
  lossesTitle: {
    fontSize: "12px",
    color: grayColor[3],
    marginBottom: "8px",
    textTransform: "uppercase",
    // letterSpacing: "0.5px",
  },
  lossItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "4px 0",
    fontSize: "12px",
    color: grayColor[2],
  },
  lossItemIndent: {
    paddingLeft: "16px",
    fontSize: "12px",
    color: grayColor[3],
  },
  lossValue: {
    color: warningColor[1],
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  // Sections content
  sectionsWrapper: {
    padding: "16px",
  },
  sectionItem: {
    marginBottom: "16px",
    "&:last-child": {
      marginBottom: "0",
    },
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: "8px",
    color: grayColor[2],
    fontSize: "12px",
    fontWeight: 500,
  },
  sectionTreeLine: {
    color: grayColor[4],
    marginRight: "8px",
    fontFamily: "monospace",
  },
  sectionName: {
    color: (props) => props.clientColor || grayColor[1],
  },
  // Entry styles
  entryItem: {
    marginLeft: "24px",
    marginBottom: "8px",
    padding: "8px 12px",
    borderRadius: "3px",
    fontSize: "12px",
    "&:last-child": {
      marginBottom: "0",
    },
  },
  entryQuality: {
    backgroundColor: `${warningColor[1]}08`,
    borderLeft: `3px solid ${warningColor[2]}`,
  },
  entryLoss: {
    backgroundColor: `${warningColor[1]}10`,
    borderLeft: `3px solid ${warningColor[1]}`,
  },
  entryCascade: {
    backgroundColor: grayColor[8],
    borderLeft: `3px solid ${grayColor[4]}`,
  },
  entryInfo: {
    backgroundColor: grayColor[8],
    borderLeft: `3px solid ${grayColor[4]}`,
  },
  entryTag: {
    display: "inline-block",
    fontSize: "11px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginRight: "8px",
    padding: "1px 4px",
    borderRadius: "2px",
  },
  tagQuality: {
    backgroundColor: `${warningColor[2]}30`,
    color: warningColor[1],
  },
  tagLoss: {
    backgroundColor: `${warningColor[1]}20`,
    color: warningColor[0],
  },
  tagCascade: {
    backgroundColor: grayColor[4],
    color: grayColor[1],
  },
  tagInfo: {
    backgroundColor: grayColor[4],
    color: grayColor[2],
  },
  entryMessage: {
    color: grayColor[1],
    display: "inline",
  },
  entryStats: {
    marginTop: "4px",
    fontSize: "11px",
    color: grayColor[1],
  },
  entryStatHighlight: {
    color: warningColor[1],
    fontWeight: 500,
  },
  // Elements/examples
  entryElements: {
    marginTop: "6px",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    backgroundColor: grayColor[6],
    padding: "6px 8px",
    borderRadius: "2px",
  },
  elementsList: {
    fontSize: "11px",
    color: grayColor[3],
    fontFamily: "'Roboto Mono', monospace",
    wordBreak: "break-all",
    flex: 1,
  },
  elementsMore: {
    color: grayColor[4],
    fontStyle: "italic",
  },
  copyButton: {
    padding: "2px",
    marginLeft: "8px",
    "&:hover": {
      // backgroundColor: grayColor[0],
      transform: "scale(1.1)",
    },
  },
  copyIcon: {
    fontSize: "14px",
    color: grayColor[9],
  },
  // Related inputs
  relatedInputs: {
    marginTop: "4px",
    fontSize: "11px",
    color: grayColor[1],
  },
  relatedInputHighlight: {
    color: warningColor[1],
    fontWeight: 500,
  },
  // Section conclusion
  sectionConclusion: {
    marginLeft: "24px",
    marginTop: "8px",
    padding: "6px 12px",
    fontSize: "11px",
    color: grayColor[2],
    borderLeft: `2px solid ${grayColor[5]}`,
  },
  conclusionArrow: {
    color: grayColor[3],
    marginRight: "6px",
  },
  // Cascade table
  cascadeTable: {
    marginTop: "6px",
    fontSize: "11px",
    width: "100%",
  },
  cascadeRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "2px 0",
    borderBottom: `1px dotted ${grayColor[5]}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  cascadeKey: {
    color: grayColor[1],
  },
  cascadeValue: {
    color: grayColor[0],
    fontWeight: 500,
  },
  cascadeNotAllocated: {
    backgroundColor: `${warningColor[1]}10`,
    padding: "4px 8px",
    marginTop: "4px",
    borderRadius: "2px",
    color: warningColor[1],
  },
  // Examples table
  examplesTable: {
    width: "100%",
    fontSize: "11px",
    marginTop: "6px",
    borderCollapse: "collapse",
    "& th": {
      padding: "4px 6px",
      textAlign: "left",
      borderBottom: `1px solid ${grayColor[5]}`,
      color: grayColor[3],
      fontWeight: 500,
      fontSize: "10px",
      textTransform: "uppercase",
    },
    "& td": {
      padding: "4px 6px",
      borderBottom: `1px solid ${grayColor[6]}`,
      color: grayColor[2],
    },
  },
  // Summary card (chapter zero)
  summaryCard: {
    marginBottom: "20px",
    padding: "8px 20px",
    backgroundColor: grayColor[13],
    // border: (props) => `1px solid ${props.clientColor || grayColor[2]}30`,
    borderRadius: "6px",
  },
  summaryCardTitle: {
    fontSize: "12px",
    fontWeight: 600,
    color: grayColor[2],
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "12px",
  },
  summaryCardGrid: {
    display: "flex",
    gap: "32px",
    flexWrap: "wrap",
  },
  summaryCardItem: {
    flex: "1 1 auto",
    minWidth: "150px",
  },
  summaryCardLabel: {
    fontSize: "11px",
    color: grayColor[3],
    marginBottom: "4px",
  },
  summaryCardValue: {
    fontSize: "18px",
    fontWeight: 600,
    color: grayColor[1],
  },
  summaryCardSubvalue: {
    fontSize: "12px",
    color: grayColor[3],
    marginTop: "2px",
  },
  summaryCardValueLoss: {
    color: warningColor[1],
  },
  // No data
  noData: {
    textAlign: "center",
    padding: "40px",
    color: grayColor[3],
    fontSize: "14px",
  },
}));

const formatNumber = (num, decimals = 0) => {
  if (num === undefined || num === null) return "-";
  return num.toLocaleString("es-CL", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatCurrency = (num) => {
  if (num === undefined || num === null) return "-";
  if (Math.abs(num) >= 1e9) {
    return `$${(num / 1e9).toFixed(2)}B`;
  }
  if (Math.abs(num) >= 1e6) {
    return `$${(num / 1e6).toFixed(1)}M`;
  }
  return `$${formatNumber(num)}`;
};

// Translation dictionary for UI terms
const TRANSLATIONS = {
  // Summary card
  calculation_summary: {
    es: "Resumen del Calculo",
    en: "Calculation Summary",
    pt: "Resumo do Calculo",
  },
  total_sales: {
    es: "Ventas totales",
    en: "Total sales",
    pt: "Vendas totais",
  },
  revenue_lost: {
    es: "Receita perdida",
    en: "Revenue lost",
    pt: "Receita perdida",
  },
  cost_not_assigned: {
    es: "Costo no asignado",
    en: "Cost not assigned",
    pt: "Custo nao alocado",
  },
  of_sales: {
    es: "de las ventas",
    en: "of sales",
    pt: "das vendas",
  },
  // Chapter
  inputs: {
    es: "inputs",
    en: "inputs",
    pt: "inputs",
  },
  coverage: {
    es: "asignación",
    en: "assignment",
    pt: "alocação",
  },
  losses: {
    es: "Perdidas",
    en: "Losses",
    pt: "Perdas",
  },
  data_quality: {
    es: "Calidad de datos",
    en: "Data quality",
    pt: "Qualidade de dados",
  },
  no_losses: {
    es: "sin perdidas",
    en: "no losses",
    pt: "sem perdas",
  },
  revenue_loss_pct: {
    es: "perdida rec",
    en: "rev loss",
    pt: "perda rec",
  },
  cost_loss_pct: {
    es: "perdida costo",
    en: "cost loss",
    pt: "perda custo",
  },
  of_total_lost: {
    es: "del total",
    en: "of total",
    pt: "do total",
  },
  of_section: {
    es: "de la sección",
    en: "of section",
    pt: "da seção",
  },
  of_chapter: {
    es: "del capítulo",
    en: "of chapter",
    pt: "do capítulo",
  },
  // Tooltips
  tooltip_revenue_loss: {
    es: "Valor de la receita no cubierto",
    en: "Revenue value not covered",
    pt: "Valor da receita nao coberto",
  },
  tooltip_cost_loss: {
    es: "Costo registrado que no fue posible asignar",
    en: "Recorded cost that could not be assigned",
    pt: "Custo registrado que nao foi possivel alocar",
  },
  // Entry stats
  lost_value: {
    es: "valor perdido",
    en: "lost value",
    pt: "valor perdido",
  },
  no_monetary_loss: {
    es: "sin pérdida monetaria en la asignación",
    en: "no monetary loss in allocation",
    pt: "sem perda monetária na alocação",
  },
  of_total: {
    es: "del total",
    en: "of total",
    pt: "do total",
  },
  duplicates: {
    es: "duplicados",
    en: "duplicates",
    pt: "duplicados",
  },
  total: {
    es: "total",
    en: "total",
    pt: "total",
  },
  not_assigned: {
    es: "No asignado",
    en: "Not assigned",
    pt: "Nao alocado",
  },
  not_processed: {
    es: "Sin procesar",
    en: "Not processed",
    pt: "Nao processado",
  },
  // Elements
  example: {
    es: "ej",
    en: "ex",
    pt: "ex",
  },
  copy_values: {
    es: "Copiar valores",
    en: "Copy values",
    pt: "Copiar valores",
  },
  input_to_review: {
    es: "input a revisar",
    en: "input to review",
    pt: "input a revisar",
  },
  // Tags
  quality: {
    es: "CALIDAD",
    en: "QUALITY",
    pt: "QUALIDADE",
  },
  loss: {
    es: "PERDIDA",
    en: "LOSS",
    pt: "PERDA",
  },
  cascade: {
    es: "CASCADA",
    en: "CASCADE",
    pt: "CASCATA",
  },
  // No data
  no_log_available: {
    es: "No hay log de calculo disponible para este periodo",
    en: "No calculation log available for this period",
    pt: "Nenhum log de calculo disponivel para este periodo",
  },
};

export default function CalculationLogViewer({ logData, inputsDict }) {
  const themeColors = useThemeColors();
  const classes = useStyles({
    clientColor: themeColors.clientColor,
    clientTextColor: themeColors.clientTextColor,
  });
  const { t, i18n } = useTranslation();
  const [expandedChapters, setExpandedChapters] = useState({});

  // Helper to get translation
  const tr = (key) => {
    const entry = TRANSLATIONS[key];
    if (!entry) return key;
    return entry[i18n.language] || entry.es;
  };

  const data = logData;

  // Guard clause - should not happen since InputsV2 handles empty state
  if (!data) {
    return null;
  }

  const notifyCopy = () => {
    toast(t("tasks.values_copied") || "Valores copiados", {
      type: "success",
      transition: ToastSlide,
      autoClose: 1500,
    });
  };

  const getInputDisplayName = (metaName) => {
    if (!inputsDict || !inputsDict[metaName]) return metaName;
    const input = inputsDict[metaName];
    return (
      input.input_name?.[i18n.language] || input.input_name?.en || metaName
    );
  };

  const getReviewableInputs = (relatedInputs) => {
    if (!relatedInputs) return [];
    return relatedInputs.filter(
      (inp) =>
        !NON_REVIEWABLE_INPUTS.some(
          (nonRev) =>
            inp.toLowerCase().includes(nonRev) ||
            nonRev.includes(inp.toLowerCase()),
        ),
    );
  };

  const toggleChapter = (chapterId) => {
    setExpandedChapters((prev) => ({
      ...prev,
      [chapterId]: !prev[chapterId],
    }));
  };

  const getSortedElements = (elements) => {
    if (!elements) return [];
    let elementsArray = elements;
    if (typeof elements === "string") {
      elementsArray = elements
        .split(",")
        .map((e) => e.trim())
        .filter((e) => e);
    }
    if (!elementsArray || elementsArray.length === 0) return [];
    return [...elementsArray].sort((a, b) =>
      String(a).localeCompare(String(b)),
    );
  };

  // Render entry based on type
  const renderEntry = (entry, index, context = {}) => {
    const { sectionName, chapterName, chapterTotalValue, sectionsCount } =
      context;
    const entryClass = cx(classes.entryItem, {
      [classes.entryQuality]: entry.type === "quality_issue",
      [classes.entryLoss]: entry.type === "loss",
      [classes.entryCascade]:
        entry.type === "cascade" ||
        entry.type === "imputation" ||
        entry.type === "allocation",
      [classes.entryInfo]: entry.type === "info",
    });

    const tagClass = cx(classes.entryTag, {
      [classes.tagQuality]: entry.type === "quality_issue",
      [classes.tagLoss]: entry.type === "loss",
      [classes.tagCascade]:
        entry.type === "cascade" ||
        entry.type === "imputation" ||
        entry.type === "allocation",
      [classes.tagInfo]: entry.type === "info",
    });

    // Tag labels using translations
    const tagLabel =
      {
        quality_issue: tr("quality"),
        loss: tr("loss"),
        cascade: tr("cascade"),
        imputation: tr("cascade"),
        allocation: tr("cascade"),
        info: "INFO",
      }[entry.type] || "INFO";

    const sortedElements = getSortedElements(entry.elements || entry.examples);
    const displayElements = sortedElements.slice(0, MAX_EXAMPLES_DISPLAY);
    const hasMoreElements = sortedElements.length > MAX_EXAMPLES_DISPLAY;
    const reviewableInputs = getReviewableInputs(entry.related_inputs);

    // Build message with count prefix for loss/quality_issue
    const lostCount = entry.data?.lost_count;
    const removedCount = entry.data?.removed_count;
    let messageWithCount = entry.message;
    if (lostCount !== undefined) {
      messageWithCount = `${formatNumber(lostCount)} ${entry.message}`;
    } else if (removedCount !== undefined) {
      messageWithCount = `${formatNumber(removedCount)} ${entry.message}`;
    }

    return (
      <div key={index} className={entryClass}>
        <span className={tagClass}>{tagLabel}</span>
        <span className={classes.entryMessage}>{messageWithCount}</span>

        {/* Stats for quality_issue */}
        {entry.type === "quality_issue" && entry.data && (
          <div className={classes.entryStats}>
            {entry.data.removed_count !== undefined && (
              <span>
                {tr("duplicates")}: {formatNumber(entry.data.removed_count)} |{" "}
                {tr("total")}: {formatNumber(entry.data.total_count)}
                {entry.data.removed_pct !== undefined &&
                  ` (${entry.data.removed_pct.toFixed(1)}%)`}
              </span>
            )}
          </div>
        )}

        {/* Stats for loss */}
        {entry.type === "loss" && entry.data && (
          <div className={classes.entryStats}>
            {entry.data.lost_value !== undefined &&
            entry.data.lost_value > 0 ? (
              <span>
                {tr("lost_value")}:{" "}
                <span className={classes.entryStatHighlight}>
                  {formatCurrency(entry.data.lost_value)}
                </span>
                {entry.data.lost_pct !== undefined && (
                  <span style={{ opacity: 0.8, marginLeft: "6px" }}>
                    | {entry.data.lost_pct.toFixed(1)}%
                    {sectionName && ` de ${sectionName}`}
                    {sectionsCount > 1 && chapterTotalValue > 0 && (
                      <span>
                        {" | "}
                        {(
                          (entry.data.lost_value / chapterTotalValue) *
                          100
                        ).toFixed(1)}
                        %{chapterName && ` de ${chapterName}`}
                      </span>
                    )}
                  </span>
                )}
              </span>
            ) : (
              <span style={{ fontStyle: "italic", opacity: 0.8 }}>
                {tr("no_monetary_loss")}
              </span>
            )}
          </div>
        )}

        {/* Info value */}
        {entry.type === "info" && entry.data?.value !== undefined && (
          <div className={classes.entryStats}>
            <span className={classes.entryStatHighlight}>
              {entry.data.value}
              {entry.data.suffix || ""}
            </span>
          </div>
        )}

        {/* Cascade table */}
        {(entry.type === "cascade" ||
          entry.type === "imputation" ||
          entry.type === "allocation") &&
          entry.data?.steps && (
            <div className={classes.cascadeTable}>
              {entry.data.steps.map((step, i) => (
                <div key={i} className={classes.cascadeRow}>
                  <span className={classes.cascadeKey}>
                    {step.key || step.method}
                  </span>
                  <span className={classes.cascadeValue}>
                    {step.count !== undefined && formatNumber(step.count)}
                    {step.value !== undefined && formatCurrency(step.value)}
                    {step.pct !== undefined && ` (${step.pct.toFixed(1)}%)`}
                  </span>
                </div>
              ))}
              {entry.data.not_allocated_value > 0 && (
                <div className={classes.cascadeNotAllocated}>
                  {tr("not_assigned")}:{" "}
                  {formatCurrency(entry.data.not_allocated_value)}
                  {entry.data.not_allocated_pct !== undefined &&
                    ` (${entry.data.not_allocated_pct.toFixed(1)}%)`}
                </div>
              )}
              {entry.data.total_remaining > 0 && (
                <div className={classes.cascadeNotAllocated}>
                  {tr("not_processed")}:{" "}
                  {formatNumber(entry.data.total_remaining)}
                </div>
              )}
            </div>
          )}

        {/* Table examples */}
        {entry.table_columns &&
          entry.table_rows &&
          entry.table_rows.length > 0 && (
            <table className={classes.examplesTable}>
              <thead>
                <tr>
                  {entry.table_columns.map((col, i) => (
                    <th key={i}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entry.table_rows
                  .slice(0, MAX_EXAMPLES_DISPLAY)
                  .map((row, i) => (
                    <tr key={i}>
                      {row.map((cell, j) => (
                        <td key={j}>
                          {typeof cell === "number" ? formatNumber(cell) : cell}
                        </td>
                      ))}
                    </tr>
                  ))}
              </tbody>
            </table>
          )}

        {/* Simple elements list */}
        {displayElements.length > 0 && !entry.table_columns && (
          <div className={classes.entryElements}>
            <div className={classes.elementsList}>
              {tr("example")}: {displayElements.join(", ")}
              {hasMoreElements && (
                <span className={classes.elementsMore}>
                  {" "}
                  (+{sortedElements.length - MAX_EXAMPLES_DISPLAY})
                </span>
              )}
            </div>
            <CopyToClipboard
              text={sortedElements.join("\n")}
              onCopy={notifyCopy}
            >
              <Tooltip
                title={`${tr("copy_values")} (${sortedElements.length})`}
              >
                <IconButton size="small" className={classes.copyButton}>
                  <FilterNone className={classes.copyIcon} />
                </IconButton>
              </Tooltip>
            </CopyToClipboard>
          </div>
        )}

        {/* Related inputs */}
        {reviewableInputs.length > 0 && (
          <div className={classes.relatedInputs}>
            {tr("input_to_review")}:{" "}
            <span className={classes.relatedInputHighlight}>
              {reviewableInputs
                .map((inp) => getInputDisplayName(inp))
                .join(", ")}
            </span>
          </div>
        )}
      </div>
    );
  };

  // Render section
  const renderSection = (
    section,
    index,
    totalSections,
    chapterContext = {},
  ) => {
    const isLast = index === totalSections - 1;
    const treeLine = isLast ? "\u2514\u2500\u2500" : "\u251C\u2500\u2500";
    const { chapterName, chapterTotalValue } = chapterContext;

    return (
      <div key={section.id} className={classes.sectionItem}>
        <div className={classes.sectionHeader}>
          <span className={classes.sectionTreeLine}>{treeLine}</span>
          <span className={classes.sectionName}>{section.name}</span>
        </div>

        {section.entries?.map((entry, i) =>
          renderEntry(entry, i, {
            sectionName: section.name,
            chapterName,
            chapterTotalValue,
            sectionsCount: totalSections,
          }),
        )}

        {section.conclusion?.summary && (
          <div className={classes.sectionConclusion}>
            <span className={classes.conclusionArrow}>{"\u25B8"}</span>
            {section.conclusion.summary}
          </div>
        )}
      </div>
    );
  };

  // Render chapter
  const renderChapter = (chapter, index) => {
    const isExpanded = expandedChapters[chapter.id] ?? false;
    const conclusion = chapter.conclusion;

    return (
      <div key={chapter.id} className={classes.chapterCard}>
        {/* Header */}
        <div
          className={classes.chapterHeader}
          onClick={() => toggleChapter(chapter.id)}
        >
          <div className={classes.chapterTitle}>
            <div className={classes.chapterNumber}>{index + 1}</div>
            <span className={classes.chapterName}>{chapter.name}</span>
          </div>
          {/* Summary in header when collapsed */}
          {!isExpanded && conclusion && (
            <div className={classes.chapterHeaderSummary}>
              <span className={classes.headerSummaryText}>
                {conclusion.summary}
              </span>
            </div>
          )}
          <div className={classes.chapterMeta}>
            {chapter.revenue_loss_contribution > 0 && (
              <Tooltip title={tr("tooltip_revenue_loss")}>
                <span
                  className={cx(classes.lossBadge, classes.lossBadgeRevenue)}
                >
                  {conclusion?.coverage_pct !== undefined
                    ? `${(100 - conclusion.coverage_pct).toFixed(1)}%`
                    : `${(chapter.revenue_loss_contribution * 100).toFixed(1)}%`}{" "}
                  {tr("revenue_loss_pct")}
                  {conclusion?.coverage_pct !== undefined && (
                    <span style={{ opacity: 0.8, marginLeft: "4px" }}>
                      ({(chapter.revenue_loss_contribution * 100).toFixed(0)}%{" "}
                      {tr("of_total_lost")})
                    </span>
                  )}
                </span>
              </Tooltip>
            )}
            {chapter.cost_loss_contribution > 0 && (
              <Tooltip title={tr("tooltip_cost_loss")}>
                <span className={cx(classes.lossBadge, classes.lossBadgeCost)}>
                  {conclusion?.coverage_pct !== undefined
                    ? `${(100 - conclusion.coverage_pct).toFixed(1)}%`
                    : `${(chapter.cost_loss_contribution * 100).toFixed(1)}%`}{" "}
                  {tr("cost_loss_pct")}
                  {conclusion?.coverage_pct !== undefined && (
                    <span style={{ opacity: 0.8, marginLeft: "4px" }}>
                      ({(chapter.cost_loss_contribution * 100).toFixed(0)}%{" "}
                      {tr("of_total_lost")})
                    </span>
                  )}
                </span>
              </Tooltip>
            )}
            {chapter.revenue_loss_contribution === 0 &&
              chapter.cost_loss_contribution === 0 && (
                <span className={cx(classes.lossBadge, classes.lossBadgeNone)}>
                  {tr("no_losses")}
                </span>
              )}
            {isExpanded ? (
              <ExpandLess className={classes.expandIcon} />
            ) : (
              <ExpandMore className={classes.expandIcon} />
            )}
          </div>
        </div>

        {/* Expanded content */}
        <Collapse in={isExpanded}>
          {/* Inputs */}
          <div className={classes.chapterInputs}>
            <span className={classes.inputsLabel}>{tr("inputs")}:</span>
            {chapter.inputs?.map((inp) => getInputDisplayName(inp)).join(", ")}
          </div>

          {/* Summary/Conclusion */}
          {conclusion && (
            <div className={classes.chapterSummary}>
              <div className={classes.summaryLine}>{conclusion.summary}</div>
              {conclusion.coverage_pct !== undefined && (
                <div className={classes.coverageLine}>
                  {tr("coverage")}:{" "}
                  <span className={classes.coverageValue}>
                    {conclusion.coverage_pct.toFixed(1)}%
                  </span>
                </div>
              )}

              {/* Losses breakdown */}
              {conclusion.losses && conclusion.losses.length > 0 && (
                <div className={classes.lossesBreakdown}>
                  <div className={classes.lossesTitle}>
                    {tr("losses")}:{" "}
                    {formatCurrency(conclusion.total_loss_value)}
                  </div>
                  {/* Group by source */}
                  {Object.entries(
                    conclusion.losses.reduce((acc, loss) => {
                      const source = loss.source || "General";
                      if (!acc[source]) acc[source] = [];
                      acc[source].push(loss);
                      return acc;
                    }, {}),
                  ).map(([source, losses]) => (
                    <div key={source}>
                      <div className={classes.lossItem}>
                        <span>{source}</span>
                        <span className={classes.lossValue}>
                          {formatCurrency(
                            losses.reduce((sum, l) => sum + (l.value || 0), 0),
                          )}
                        </span>
                      </div>
                      {losses.map((loss, i) => (
                        <div
                          key={i}
                          className={cx(
                            classes.lossItem,
                            classes.lossItemIndent,
                          )}
                        >
                          <span>
                            - {loss.message}
                            {loss.count !== undefined &&
                              ` (${formatNumber(loss.count)})`}
                          </span>
                          <span className={classes.lossValue}>
                            {loss.value > 0
                              ? formatCurrency(loss.value)
                              : tr("no_monetary_loss")}
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {/* Quality issues summary */}
              {conclusion.quality_issues &&
                conclusion.quality_issues.length > 0 && (
                  <div className={classes.lossesBreakdown}>
                    <div className={classes.lossesTitle}>
                      {tr("data_quality")}
                    </div>
                    {conclusion.quality_issues.map((qi, i) => (
                      <div key={i} className={classes.lossItem}>
                        <span>
                          {qi.message}
                          {qi.removed !== undefined &&
                            ` (${formatNumber(qi.removed)})`}
                        </span>
                        <span style={{ color: grayColor[3] }}>{qi.source}</span>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          )}

          {/* Sections */}
          <div className={classes.sectionsWrapper}>
            {chapter.sections?.map((section, i) => {
              // Calculate chapter total value from breakdown or sum of section totals
              const chapterTotalValue = conclusion?.breakdown
                ? Object.values(conclusion.breakdown).reduce(
                    (sum, v) => sum + v,
                    0,
                  )
                : conclusion?.total_loss_value || 0;
              return renderSection(section, i, chapter.sections.length, {
                chapterName: chapter.name,
                chapterTotalValue,
              });
            })}
          </div>
        </Collapse>
      </div>
    );
  };

  if (!data) {
    return (
      <div className={classes.noData}>
        <Info style={{ fontSize: 48, marginBottom: 16, color: grayColor[4] }} />
        <div>{tr("no_log_available")}</div>
      </div>
    );
  }

  // Calculate cost loss percentage relative to revenue
  const costLossPctOfRevenue =
    data.summary?.revenue_total > 0
      ? ((data.summary?.cost_loss_value || 0) / data.summary.revenue_total) *
        100
      : 0;

  return (
    <div className={classes.root}>
      {/* Summary Card - Chapter Zero */}
      {data.summary && (
        <div className={classes.summaryCard}>
          <div className={classes.summaryCardTitle}>
            {tr("calculation_summary")}
          </div>
          <div className={classes.summaryCardGrid}>
            <div className={classes.summaryCardItem}>
              <div className={classes.summaryCardLabel}>
                {tr("total_sales")}
              </div>
              <div className={classes.summaryCardValue}>
                {formatCurrency(data.summary.revenue_total)}
              </div>
            </div>
            <div className={classes.summaryCardItem}>
              <div className={classes.summaryCardLabel}>
                {tr("revenue_lost")}
              </div>
              <div
                className={cx(
                  classes.summaryCardValue,
                  classes.summaryCardValueLoss,
                )}
              >
                {formatCurrency(data.summary.revenue_loss_value)}
              </div>
              <div className={classes.summaryCardSubvalue}>
                {data.summary.revenue_loss_pct?.toFixed(1)}% {tr("of_sales")}
              </div>
            </div>
            <div className={classes.summaryCardItem}>
              <div className={classes.summaryCardLabel}>
                {tr("cost_not_assigned")}
              </div>
              <div
                className={cx(
                  classes.summaryCardValue,
                  classes.summaryCardValueLoss,
                )}
              >
                {formatCurrency(data.summary.cost_loss_value)}
              </div>
              <div className={classes.summaryCardSubvalue}>
                {costLossPctOfRevenue.toFixed(1)}% {tr("of_sales")}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chapters */}
      {data.chapters?.map((chapter, index) => renderChapter(chapter, index))}
    </div>
  );
}

CalculationLogViewer.propTypes = {
  logData: PropTypes.object,
  inputsDict: PropTypes.object,
};

CalculationLogViewer.defaultProps = {
  logData: null,
  inputsDict: {},
};
